import React, { useState, useRef, useEffect } from 'react';
// Import Trebble wrappers
import TrebbleCard from '../../../../common/TrebbleCard';
import TrebbleTooltip from '../../../../common/TrebbleTooltip';
import TrebbleSkeleton from '../../../../common/TrebbleSkeleton';

import ti18n from 'i18n!nls/ClipCreatori18n';

declare global {
    interface Window {
        alertErrorMessage: (error: any) => void;
        getI18n: (i18nData: any, key: string, options?: any) => string;
    }
}
import {
    ExpandOutlined
} from '@ant-design/icons';
import TrebbleButton from '../../../../common/TrebbleButton';
import { getCloudFrontUrl } from '../../../../helper/CloudFrontUrlHelper';

interface FocusRegion {
    x: number;
    y: number;
    width: number;
    height: number;
    aspectRatio: string;
}

interface VideoFocusRegionProps {
    videoUrl: string;
    focusRegion: FocusRegion;
    onFocusRegionChange: (region: FocusRegion) => void;
    displayed: boolean;
    instructionLabel: string;
    resetLabel: string;
    useCard?: boolean;
}

const VideoFocusRegionSelector: React.FC<VideoFocusRegionProps> = ({
    videoUrl,
    focusRegion,
    onFocusRegionChange,
    displayed,
    instructionLabel,
    resetLabel,
    useCard = true
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState<{ x: number, y: number }>({ x: 0, y: 0 });
    const [isResizing, setIsResizing] = useState(false);
    const [activeCorner, setActiveCorner] = useState<'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | null>(null);
    const [videoAspectRatio, setVideoAspectRatio] = useState<number>(16/9); // Default to 16:9
    const [isVideoReady, setIsVideoReady] = useState(false); // Track if video is loaded and ready
    const [videoRenderedBounds, setVideoRenderedBounds] = useState({
        offsetX: 0,    // Horizontal offset as percentage (for pillarboxing)
        offsetY: 0,    // Vertical offset as percentage (for letterboxing)
        scaleX: 1,     // Horizontal scale factor
        scaleY: 1      // Vertical scale factor
    });
    const videoRef = useRef<HTMLVideoElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Parse aspect ratio with validation
    const parseAspectRatio = (aspectRatioString: string): number => {
        const parts = aspectRatioString.split(':').map(Number);
        if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1]) || parts[1] === 0) {
            console.error('Invalid aspect ratio format:', aspectRatioString, 'defaulting to 16:9');
            return 16 / 9;
        }
        return parts[0] / parts[1];
    };

    const aspectRatio = parseAspectRatio(focusRegion.aspectRatio);

    // Add a default region for reset (fallback to current focusRegion if not available)
    const defaultRegion = {
        x: focusRegion.x,
        y: focusRegion.y,
        width: focusRegion.width,
        height: focusRegion.height,
        aspectRatio: focusRegion.aspectRatio,
    };

    // Calculate where the video is actually rendered within the container (accounting for object-contain)
    const calculateVideoRenderedBounds = () => {
        const video = videoRef.current;
        const container = containerRef.current;
        if (!video || !container || !video.videoWidth || !video.videoHeight) return;

        const containerRect = container.getBoundingClientRect();
        const videoAspect = video.videoWidth / video.videoHeight;
        const containerAspect = containerRect.width / containerRect.height;

        let renderWidth, renderHeight, offsetX, offsetY;

        if (Math.abs(videoAspect - containerAspect) < 0.001) {
            // Aspects match - video fills container
            renderWidth = containerRect.width;
            renderHeight = containerRect.height;
            offsetX = 0;
            offsetY = 0;
        } else if (videoAspect > containerAspect) {
            // Video is wider than container - fits width, letterbox top/bottom
            renderWidth = containerRect.width;
            renderHeight = containerRect.width / videoAspect;
            offsetX = 0;
            offsetY = (containerRect.height - renderHeight) / 2;
        } else {
            // Video is taller than container - fits height, pillarbox left/right
            renderHeight = containerRect.height;
            renderWidth = containerRect.height * videoAspect;
            offsetY = 0;
            offsetX = (containerRect.width - renderWidth) / 2;
        }

        setVideoRenderedBounds({
            offsetX: offsetX / containerRect.width,  // As percentage of container
            offsetY: offsetY / containerRect.height,
            scaleX: renderWidth / containerRect.width,
            scaleY: renderHeight / containerRect.height
        });
    };

    const handleMouseDown = (e: React.MouseEvent, mode: 'move' | 'resize', corner?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight') => {
        e.stopPropagation();
        if (!containerRef.current) return;

        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();

        if (mode === 'move') {
            // Convert mouse position to container-relative coordinates
            const containerX = (e.clientX - containerRect.left) / containerRect.width;
            const containerY = (e.clientY - containerRect.top) / containerRect.height;

            // Convert to video-relative coordinates (accounting for offset)
            const videoX = (containerX - videoRenderedBounds.offsetX) / videoRenderedBounds.scaleX;
            const videoY = (containerY - videoRenderedBounds.offsetY) / videoRenderedBounds.scaleY;

            // Calculate drag offset relative to focus region
            const relativeX = videoX - focusRegion.x;
            const relativeY = videoY - focusRegion.y;
            setDragOffset({ x: relativeX, y: relativeY });
            setIsDragging(true);
        } else if (mode === 'resize' && corner) {
            setIsResizing(true);
            setActiveCorner(corner);
        }
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!containerRef.current || (!isDragging && !isResizing)) return;

        const container = containerRef.current;
        const containerRect = container.getBoundingClientRect();

        // Convert mouse position to container-relative coordinates
        const containerX = (e.clientX - containerRect.left) / containerRect.width;
        const containerY = (e.clientY - containerRect.top) / containerRect.height;

        // Convert to video-relative coordinates (accounting for offset)
        const videoX = (containerX - videoRenderedBounds.offsetX) / videoRenderedBounds.scaleX;
        const videoY = (containerY - videoRenderedBounds.offsetY) / videoRenderedBounds.scaleY;

        if (isDragging) {
            const newX = Math.max(0, Math.min(
                videoX - dragOffset.x,
                1 - focusRegion.width
            ));
            const newY = Math.max(0, Math.min(
                videoY - dragOffset.y,
                1 - focusRegion.height
            ));

            onFocusRegionChange({
                ...focusRegion,
                x: newX,
                y: newY
            });
        }

        if (isResizing && activeCorner) {
            const video = videoRef.current;
            if (!video || !video.videoWidth || !video.videoHeight) return;

            let newX = focusRegion.x;
            let newY = focusRegion.y;
            let newWidth = focusRegion.width;
            let newHeight = focusRegion.height;

            // Calculate video's actual aspect ratio
            const sourceVideoAspectRatio = video.videoWidth / video.videoHeight;

            // To maintain target aspect ratio in normalized coords:
            // newHeight = newWidth * (sourceVideoAspectRatio / targetAspectRatio)
            // This accounts for the video's dimensions when converting normalized coords to pixels
            const aspectRatioFactor = sourceVideoAspectRatio / aspectRatio;

            switch (activeCorner) {
                case 'topLeft':
                    newWidth = focusRegion.x + focusRegion.width - videoX;
                    newX = videoX;
                    newHeight = newWidth * aspectRatioFactor;
                    newY = focusRegion.y + focusRegion.height - newHeight;
                    break;
                case 'topRight':
                    newWidth = videoX - focusRegion.x;
                    newHeight = newWidth * aspectRatioFactor;
                    newY = focusRegion.y + focusRegion.height - newHeight;
                    break;
                case 'bottomLeft':
                    newWidth = focusRegion.x + focusRegion.width - videoX;
                    newX = videoX;
                    newHeight = newWidth * aspectRatioFactor;
                    break;
                case 'bottomRight':
                    newWidth = videoX - focusRegion.x;
                    newHeight = newWidth * aspectRatioFactor;
                    break;
            }

            if (newWidth >= 0.1 && newHeight >= 0.1 &&
                newX >= 0 && newY >= 0 &&
                newX + newWidth <= 1 && newY + newHeight <= 1) {
                onFocusRegionChange({
                    ...focusRegion,
                    x: newX,
                    y: newY,
                    width: newWidth,
                    height: newHeight
                });
            }
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        setIsResizing(false);
        setActiveCorner(null);
    };

    useEffect(() => {
        if (isDragging || isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            return () => {
                window.removeEventListener('mousemove', handleMouseMove);
                window.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, isResizing, dragOffset, activeCorner]);

    // Pause/reset video when modal closes
    useEffect(() => {
        if (!displayed && videoRef.current) {
            videoRef.current.pause();
            videoRef.current.currentTime = 0;
        }
    }, [displayed]);

    // Detect video dimensions when loaded and calculate rendered bounds
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        // Reset ready state when video URL changes
        setIsVideoReady(false);

        const handleLoadedMetadata = () => {
            if (video.videoWidth && video.videoHeight) {
                const detectedAspectRatio = video.videoWidth / video.videoHeight;
                setVideoAspectRatio(detectedAspectRatio);
                // Calculate where video is rendered within container
                calculateVideoRenderedBounds();
                // Mark video as ready
                setIsVideoReady(true);
            }
        };

        const handleError = () => {
            console.error('Failed to load video:', videoUrl);
            // Show video anyway to display native error UI
            setIsVideoReady(true);
            if (typeof window !== 'undefined' && window.alertErrorMessage) {
                window.alertErrorMessage('Failed to load video for focus region editing.');
            }
        };

        const handleResize = () => {
            calculateVideoRenderedBounds();
        };

        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('error', handleError);
        window.addEventListener('resize', handleResize);

        // If video is already loaded
        if (video.readyState >= 1) {
            handleLoadedMetadata();
        }

        return () => {
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('error', handleError);
            window.removeEventListener('resize', handleResize);
        };
    }, [videoUrl]);

    const content = (
        <>
            {/* Instructional message */}
            <div className="text-sm text-gray-900 dark:text-gray-100 bg-gray-200 dark:bg-gray-800 p-4 my-4">
                {instructionLabel}
            </div>

            <div
                ref={containerRef}
                className="relative w-full bg-gray-100 rounded-md overflow-hidden"
                style={{ height: '70vh' }}
            >
                {/* Video element - always rendered to allow metadata loading */}
                <video
                    ref={videoRef}
                    src={getCloudFrontUrl(videoUrl)}
                    className="absolute inset-0 w-full h-full object-contain"
                    style={{ visibility: isVideoReady ? 'visible' : 'hidden' }}
                    controls
                    crossOrigin="anonymous"
                />

                {/* Loading skeleton */}
                {!isVideoReady && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                        <TrebbleSkeleton active paragraph={{ rows: 8 }} />
                    </div>
                )}

                {/* Overlay and selection box - only when video is ready */}
                {isVideoReady && (
                    <>
                        {/* Dark overlay with cutout */}
                        <div className="absolute inset-0 bg-black/30 pointer-events-none">
                            <div
                                style={{
                                    position: 'absolute',
                                    left: `${(videoRenderedBounds.offsetX + focusRegion.x * videoRenderedBounds.scaleX) * 100}%`,
                                    top: `${(videoRenderedBounds.offsetY + focusRegion.y * videoRenderedBounds.scaleY) * 100}%`,
                                    width: `${focusRegion.width * videoRenderedBounds.scaleX * 100}%`,
                                    height: `${focusRegion.height * videoRenderedBounds.scaleY * 100}%`,
                                    background: 'transparent',
                                    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)'
                                }}
                            />
                        </div>

                        {/* Focus region selection box */}
                        <div
                            className="absolute cursor-move"
                            style={{
                                left: `${(videoRenderedBounds.offsetX + focusRegion.x * videoRenderedBounds.scaleX) * 100}%`,
                                top: `${(videoRenderedBounds.offsetY + focusRegion.y * videoRenderedBounds.scaleY) * 100}%`,
                                width: `${focusRegion.width * videoRenderedBounds.scaleX * 100}%`,
                                height: `${focusRegion.height * videoRenderedBounds.scaleY * 100}%`,
                            }}
                            onMouseDown={(e) => handleMouseDown(e, 'move')}
                        >
                            {/* Animated dotted border */}
                            <div className="absolute inset-0 pointer-events-none animated-border" />

                            {/* Corner handles with tooltips */}
                            <TrebbleTooltip title={window.getI18n ? window.getI18n(ti18n, 'RESIZE_FROM_HERE') : ti18n.RESIZE_FROM_HERE}>
                                <div
                                    className="corner-handle top-0 left-0 cursor-nw-resize"
                                    onMouseDown={(e) => handleMouseDown(e, 'resize', 'topLeft')}
                                />
                            </TrebbleTooltip>
                            <TrebbleTooltip title={window.getI18n ? window.getI18n(ti18n, 'RESIZE_FROM_HERE') : ti18n.RESIZE_FROM_HERE}>
                                <div
                                    className="corner-handle top-0 left-full cursor-ne-resize"
                                    onMouseDown={(e) => handleMouseDown(e, 'resize', 'topRight')}
                                />
                            </TrebbleTooltip>
                            <TrebbleTooltip title={window.getI18n ? window.getI18n(ti18n, 'RESIZE_FROM_HERE') : ti18n.RESIZE_FROM_HERE}>
                                <div
                                    className="corner-handle top-full left-0 cursor-sw-resize"
                                    onMouseDown={(e) => handleMouseDown(e, 'resize', 'bottomLeft')}
                                />
                            </TrebbleTooltip>
                            <TrebbleTooltip title={window.getI18n ? window.getI18n(ti18n, 'RESIZE_FROM_HERE') : ti18n.RESIZE_FROM_HERE}>
                                <div
                                    className="corner-handle top-full left-full cursor-se-resize"
                                    onMouseDown={(e) => handleMouseDown(e, 'resize', 'bottomRight')}
                                />
                            </TrebbleTooltip>
                        </div>
                    </>
                )}
            </div>
        </>
    );

    if (!useCard) {
        return content;
    }

    return (
        <TrebbleCard
            title="Video Focus Region Selector"
            extra={
                <TrebbleTooltip title="Drag to move, resize from any corner">
                    <ExpandOutlined />
                </TrebbleTooltip>
            }
            className="w-full mx-auto"
        >
            {content}
        </TrebbleCard>
    );
};

export default VideoFocusRegionSelector;
